import awsS3Logo from "core/assets/icons/integrations/aws-s3.svg";
import azureBlobLogo from "core/assets/icons/integrations/azure-blob.svg";
import databricksLogo from "core/assets/icons/integrations/databricks.svg";
import gcsLogo from "core/assets/icons/integrations/gcs.svg";
import snowflakeLogo from "core/assets/icons/integrations/snowflake.svg";

export type FsProvider = {
  id: string;
  name: string;
  description: string;
};

export const providers: FsProvider[] = [
  {
    id: "s3",
    name: "Amazon S3",
    description: "Managed object storage on AWS",
  },
  {
    id: "azure-blob",
    name: "Azure Blob Storage",
    description: "Managed object storage on Microsoft Azure",
  },
  {
    id: "databricks-volumes",
    name: "Databricks Unity Catalog Volumes",
    description: "File storage governed through Unity Catalog",
  },
  {
    id: "gcs",
    name: "Google Cloud Storage",
    description: "Managed object storage on Google Cloud",
  },
  {
    id: "snowflake-stage",
    name: "Snowflake Stage",
    description: "Managed file storage on Snowflake",
  },
];

export type ConnectionStatus =
  | "connected"
  | "degraded"
  | "not_connected"
  | "validation_failed"
  | "validating";

export type DataTagRow = {
  id: string;
  name: string;
  displayName: string;
  status: ConnectionStatus;
  activeStores: number | null;
  lastValidated: string | null;
};

export type OpaAgent = {
  id: string;
  name: string;
  environment: string;
  host: string | null;
  lastSeen: string | null;
  status: ConnectionStatus;
  provider: string;
};

export type ValidationCheck = {
  id: string;
  name: string;
  detail: string | null;
  status: "failed" | "passed" | "pending" | "running" | "skipped";
};

export const statusConfig: Record<
  ConnectionStatus,
  { label: string; dotClass: string; textClass: string }
> = {
  connected: {
    label: "Connected",
    dotClass: "bg-emerald-500",
    textClass: "text-emerald-700",
  },
  degraded: {
    label: "Degraded",
    dotClass: "bg-amber-500",
    textClass: "text-amber-700",
  },
  not_connected: {
    label: "Not connected",
    dotClass: "bg-gray-400",
    textClass: "text-gray-500",
  },
  validation_failed: {
    label: "Validation failed",
    dotClass: "bg-red-500",
    textClass: "text-red-700",
  },
  validating: {
    label: "Validating",
    dotClass: "bg-blue-500 animate-pulse",
    textClass: "text-blue-700",
  },
};

export const initialDataTags: DataTagRow[] = [
  {
    id: "dev",
    name: "dev",
    displayName: "Development",
    status: "not_connected",
    activeStores: null,
    lastValidated: null,
  },
  {
    id: "prod",
    name: "prod",
    displayName: "Production",
    status: "not_connected",
    activeStores: null,
    lastValidated: null,
  },
  {
    id: "staging",
    name: "staging",
    displayName: "Staging",
    status: "not_connected",
    activeStores: null,
    lastValidated: null,
  },
  {
    id: "preview",
    name: "preview",
    displayName: "Preview",
    status: "not_connected",
    activeStores: null,
    lastValidated: null,
  },
];

export const initialAgents: OpaAgent[] = [
  {
    id: "dev-agent",
    name: "Development",
    environment: "Development",
    host: "opa-dev.internal.acme.com",
    lastSeen: "2m ago",
    status: "connected",
    provider: "Amazon S3",
  },
  {
    id: "prod-agent",
    name: "Production",
    environment: "Production",
    host: "opa-prod.internal.acme.com",
    lastSeen: "1m ago",
    status: "connected",
    provider: "Amazon S3",
  },
];

export const sampleIntegrations = [
  { id: "int-1", name: "acme-dev-aws" },
  { id: "int-2", name: "acme-prod-aws" },
  { id: "int-3", name: "acme-staging-aws" },
];

export type ProviderId =
  | "azure-blob"
  | "databricks-volumes"
  | "gcs"
  | "s3"
  | "snowflake-stage";

export type DataPlaneCopy = {
  accountNoun: string;
  cloneRepoUrl: string;
};

export const providerCopy: Record<ProviderId, DataPlaneCopy> = {
  "azure-blob": {
    accountNoun: "your Azure subscription",
    cloneRepoUrl:
      "https://github.com/superblocksteam/byo-file-storage-azure-blob-terraform",
  },
  "databricks-volumes": {
    accountNoun: "your Databricks workspace",
    cloneRepoUrl:
      "https://github.com/superblocksteam/byo-file-storage-databricks-volumes-terraform",
  },
  gcs: {
    accountNoun: "your Google Cloud project",
    cloneRepoUrl:
      "https://github.com/superblocksteam/byo-file-storage-gcs-terraform",
  },
  s3: {
    accountNoun: "your AWS account",
    cloneRepoUrl:
      "https://github.com/superblocksteam/byo-file-storage-aws-s3-terraform",
  },
  "snowflake-stage": {
    accountNoun: "your Snowflake account",
    cloneRepoUrl:
      "https://github.com/superblocksteam/byo-file-storage-snowflake-stage-terraform",
  },
};

export const providerLogos: Record<ProviderId, string> = {
  "azure-blob": azureBlobLogo,
  "databricks-volumes": databricksLogo,
  gcs: gcsLogo,
  s3: awsS3Logo,
  "snowflake-stage": snowflakeLogo,
};

export function getProviderIdFromName(name: string): ProviderId {
  const match = providers.find((p) => p.name === name);
  return (match?.id ?? "s3") as ProviderId;
}

export function getYamlConfig(dataTag: string, providerId: ProviderId): string {
  switch (providerId) {
    case "azure-blob":
      return `superblocks:
  lifecycleProfiles:
    - id: azure-blob-${dataTag}
      type: file_storage
      environmentClass: deployed
      environmentName: ${dataTag}
      provider: azure_blob
      module:
        source: superblocksteam/byo-file-storage-blob/azurerm
        version: "~> 0.1"
      cloud:
        azure:
          subscriptionId: "<your-subscription-id>"
          resourceGroup: "<your-resource-group>"
          storageAccount: "<your-storage-account>"
          container: "<your-container>"
          keyPrefix: "superblocks/file-storage/${dataTag}"
      state:
        backend: azurerm
        resourceGroupName: "<your-state-resource-group>"
        storageAccountName: "<your-state-storage-account>"
        containerName: superblocks-tf-state
        keyPrefix: superblocks/byo-files/${dataTag}
      policies:
        autoApply: true
        deletionProtection: true
        softDeleteWindow: 14d`;
    case "databricks-volumes":
      return `superblocks:
  lifecycleProfiles:
    - id: databricks-volumes-${dataTag}
      type: file_storage
      environmentClass: deployed
      environmentName: ${dataTag}
      provider: databricks_unity_volumes
      module:
        source: superblocksteam/byo-file-storage-unity-volumes/databricks
        version: "~> 0.1"
      cloud:
        databricks:
          host: "https://<workspace-id>.cloud.databricks.com"
          catalog: "<unity-catalog-name>"
          schema: "<schema-name>"
          volume: "<volume-name>"
          keyPrefix: "superblocks/file-storage/${dataTag}"
      state:
        backend: databricks_volume
        volume: "/Volumes/<catalog>/<schema>/superblocks-state"
        keyPrefix: superblocks/byo-files/${dataTag}
      policies:
        autoApply: true
        deletionProtection: true
        softDeleteWindow: 14d`;
    case "gcs":
      return `superblocks:
  lifecycleProfiles:
    - id: gcs-${dataTag}
      type: file_storage
      environmentClass: deployed
      environmentName: ${dataTag}
      provider: gcs
      module:
        source: superblocksteam/byo-file-storage-gcs/google
        version: "~> 0.1"
      cloud:
        google:
          projectId: "<your-project-id>"
          serviceAccount: "<your-service-account-email>"
          bucket: "<your-bucket>"
          location: "<your-region>"
          keyPrefix: "superblocks/file-storage/${dataTag}"
      state:
        backend: gcs
        bucket: "<your-state-bucket>"
        keyPrefix: superblocks/byo-files/${dataTag}
      policies:
        autoApply: true
        deletionProtection: true
        softDeleteWindow: 14d`;
    case "snowflake-stage":
      return `superblocks:
  lifecycleProfiles:
    - id: snowflake-stage-${dataTag}
      type: file_storage
      environmentClass: deployed
      environmentName: ${dataTag}
      provider: snowflake_stage
      module:
        source: superblocksteam/byo-file-storage-stage/snowflake
        version: "~> 0.1"
      cloud:
        snowflake:
          account: "<your-account-locator>"
          region: "<your-region>"
          role: "<your-role>"
          warehouse: "<your-warehouse>"
          database: SUPERBLOCKS_FILES
          schema: FILES_${dataTag.toUpperCase()}
          stage: "@SUPERBLOCKS_FILES.${dataTag.toUpperCase()}.FILE_STAGE"
      state:
        backend: snowflake_stage
        stage: "@SUPERBLOCKS_FILES.STATE.TF_STATE"
        keyPrefix: superblocks/byo-files/${dataTag}
      policies:
        autoApply: true
        deletionProtection: true
        softDeleteWindow: 14d`;
    case "s3":
    default:
      return `superblocks:
  lifecycleProfiles:
    - id: s3-${dataTag}
      type: file_storage
      environmentClass: deployed
      environmentName: ${dataTag}
      provider: aws_s3
      module:
        source: superblocksteam/byo-file-storage-s3/aws
        version: "~> 0.1"
      cloud:
        aws:
          accountId: "<your-account-id>"
          region: "<your-region>"
          assumeRoleArn: "<role-arn>"
          bucket: "<your-bucket>"
          keyPrefix: "superblocks/file-storage/${dataTag}"
      state:
        backend: s3
        bucket: "<your-state-bucket>"
        keyPrefix: superblocks/byo-files/${dataTag}
      policies:
        autoApply: true
        deletionProtection: true
        softDeleteWindow: 14d`;
  }
}

export function getValidationChecks(dataTag: string): ValidationCheck[] {
  const isFailing = dataTag === "staging";

  return [
    {
      id: "credentials",
      name: "Integration credentials valid",
      detail: "acme-staging-aws",
      status: "pending",
    },
    {
      id: "parent-path",
      name: "Parent path exists",
      detail: "s3://acme-files-staging/superblocks",
      status: "pending",
    },
    {
      id: "provider-reachable",
      name: "Provider reachable",
      detail: "s3.us-east-1.amazonaws.com",
      status: "pending",
    },
    {
      id: "read-permission",
      name: "Read permission confirmed",
      detail: isFailing ? "Skipped — fix above first" : null,
      status: "pending",
    },
    {
      id: "write-permission",
      name: "Write permission confirmed",
      detail: isFailing
        ? "AccessDenied: s3:PutObject denied for prefix superblocks/.validation/. Grant write permission to the role used by integration acme-staging-aws."
        : null,
      status: "pending",
    },
  ];
}
